import React, { FC, useState, Fragment } from 'react';

import * as CSS from '../../match-entry-styles';
import { Course, PlayerResult, TeamGameMatch } from '../../../../../database.schema';
import { HoleScore } from '../hole-score';
import { FlexColumnCentred, FlexRowSpaceBetween } from '../../../common-styles/common-styles';
import { BACK_NINE_INDEX, FRONT_NINE_INDEX, MATCH_FORMATS } from '../../../../app/constants';
import { TeamGamePointsConfirmation } from './team-game-points-confirmation';
import { getHoleTierPoints6_6_6, getHoleTierPoints1_2_3 } from '../../../../utils/team-games-score-calculator';

type Props = {
  recordedMatch: TeamGameMatch;
  course: Course;
  setRecordedMatch: (value: TeamGameMatch) => void;
  setShowScoreEntry: (value: boolean) => void;
  startingHole: number;
  teamIndex: number;
};

const getFormatHolePoints = (players: Array<PlayerResult>, holIndex: number, course: Course, startingHole: number, matchFormat: string) => {
  return matchFormat === MATCH_FORMATS.tierPoints_6_6_6.id
    ? getHoleTierPoints6_6_6(players, holIndex, course, startingHole)
    : getHoleTierPoints1_2_3(players, holIndex, course, startingHole);
};

export const TeamGameHoleScores: FC<Props> = ({ teamIndex, setShowScoreEntry, recordedMatch, course, setRecordedMatch, startingHole }): JSX.Element => {
  const [showConfirm, setShowConfirm] = useState(false);
  const holeIndexes = startingHole === 1 ? [...FRONT_NINE_INDEX, ...BACK_NINE_INDEX] : [...BACK_NINE_INDEX, ...FRONT_NINE_INDEX];
  const [holeIndex, setHoleIndex] = useState(0);
  const [holePar, setHolePar] = useState(course.scoreCard[holeIndexes[0]]);
  const [holeSI, setHoleSI] = useState(course.strokeIndex[holeIndexes[0]]);
  const [recordedMatchStore, setRecordedMatchStore] = useState(recordedMatch);
  const [teamPlayers, setTeamPlayers] = useState<Array<PlayerResult>>(recordedMatch.teams[teamIndex].players);
  const [holePoints, setHolePoints] = useState(getFormatHolePoints(recordedMatch.teams[teamIndex].players, 0, course, startingHole, recordedMatch.matchFormat));

  const formatName = recordedMatch.matchFormat === MATCH_FORMATS.tierPoints_6_6_6.id ? 'Tier Points 6-6-6' : 'Tier Points 1-2-3';
  const setHoleData = (newIndex: number) => {
    setHoleIndex(newIndex);
    setHolePar(course.scoreCard[holeIndexes[newIndex]]);
    setHoleSI(course.strokeIndex[holeIndexes[newIndex]]);
  };

  const decrementHole = () => {
    const newIndex = holeIndex - 1;
    setHoleData(newIndex);
  };
  const incrementHole = () => {
    const newIndex = holeIndex + 1;
    setHoleData(newIndex);
    if (newIndex === 9) {
      setShowConfirm(!showConfirm);
    }
  };

  const setPlayerData = (playerId: number, recordedScore: number) => {
    const playerIndex = teamPlayers.findIndex((player: PlayerResult) => player.playerId === playerId);
    const round = teamPlayers[playerIndex].round;
    if (round) {
      const newScoreCard = round.scoreCard ? [...round.scoreCard] : [];
      newScoreCard.splice(holeIndex, 1, recordedScore);
      const newTeamPlayer = { ...teamPlayers[playerIndex], round: { ...round, scoreCard: newScoreCard } };
      const players = [...teamPlayers];
      players.splice(playerIndex, 1, newTeamPlayer);
      setTeamPlayers([...players]);
      setHolePoints(getFormatHolePoints(players, holeIndex, course, startingHole, recordedMatch.matchFormat));
    }
  };

  const saveScoresAndShowConfirm = () => {
    const updatedRecordedMatch = { ...recordedMatchStore };
    updatedRecordedMatch.teams.splice(teamIndex, 1, { ...recordedMatchStore.teams[teamIndex], players: teamPlayers });
    setRecordedMatch({ ...updatedRecordedMatch });
    setShowConfirm(true);
  };
  if (showConfirm) {
    return (
      <TeamGamePointsConfirmation
        startingHole={startingHole}
        recordedTierPointsTeamPlayers={teamPlayers}
        holeIndex={holeIndex}
        matchFormat={recordedMatch.matchFormat}
        course={course}
        setShowConfirm={setShowConfirm}
        setShowScoreEntry={setShowScoreEntry}
        decrementHole={decrementHole}
      />
    );
  }

  return (
    <Fragment>
      <h2>Score Entry</h2>
      <FlexRowSpaceBetween>
        <h3>Course: {course.name}</h3>
      </FlexRowSpaceBetween>
      <CSS.ScoreEntryWrapper data-testid="score-entry">
        <CSS.MiddleSection>
          <CSS.HoleInfoTitle>Hole</CSS.HoleInfoTitle>
          <CSS.FlexRow>
            <CSS.HoleNavigationButton onClick={() => decrementHole()} disabled={holeIndex === 0}>
              {`<< previous`}
            </CSS.HoleNavigationButton>
            <CSS.HoleNumber>{holeIndexes[holeIndex] + 1}</CSS.HoleNumber>{' '}
            {holeIndex < 17 && (
              <CSS.HoleNavigationButton data-testid="increment-hole" onClick={() => incrementHole()} disabled={holeIndex === 17}>
                {`next >>`}
              </CSS.HoleNavigationButton>
            )}
            {holeIndex === 17 && (
              <CSS.HoleNavigationButton data-testid="finish-button" onClick={() => saveScoresAndShowConfirm()}>
                FINISH
              </CSS.HoleNavigationButton>
            )}
          </CSS.FlexRow>
          <CSS.FlexRow>
            <CSS.HoleInfoTitle>{`Par: ${holePar}`}</CSS.HoleInfoTitle>
            <CSS.HoleInfoTitle>
              SI: <CSS.SI>{holeSI}</CSS.SI>
            </CSS.HoleInfoTitle>
          </CSS.FlexRow>
          <CSS.DivWithMargin borderColour="red">
            <FlexColumnCentred>
              {teamPlayers.map((player: PlayerResult) => (
                <HoleScore
                  key={`key-${player.playerId}`}
                  playerId={player.playerId}
                  handicap={player.round.individualHandicap}
                  strokeIndex={holeSI}
                  par={holePar}
                  score={player.round.scoreCard[holeIndex]}
                  setPlayerData={setPlayerData}
                  team="team1"
                  matchShots={0}
                />
              ))}
            </FlexColumnCentred>
            {recordedMatch.matchFormat !== MATCH_FORMATS.stableford.id && (
              <FlexColumnCentred>
                <CSS.TierPointsHoleTotal>
                  {formatName}: {holePoints}
                </CSS.TierPointsHoleTotal>
              </FlexColumnCentred>
            )}
          </CSS.DivWithMargin>
        </CSS.MiddleSection>
      </CSS.ScoreEntryWrapper>
    </Fragment>
  );
};
